import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { usePrevious } from "../utils/previous-hook";
import { closeNitrum, openDrawer } from "../store/navSlice";

import NavNitrum from "./NavNitrum";
import configMap from "../data/config";
import materialsMap from "../data/materials";

import "./YourNitrum.scss";
import sunriseLogo from "../assets/images/sunrise-medical-logo.png";
import nitrumLogo from "../assets/images/nitrum-logo.png";
import { ReactComponent as IconCaret } from "../assets/images/caret.svg";
import { ReactComponent as IconPencil } from "../assets/images/pencil.svg";

const AttrDisabledException = {};

function Section({ partKey }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const config = useSelector((state) => state.config);
  const attributes = configMap[partKey];

  const getAttribute = (attrKey) => {
    const attr = configMap[partKey][attrKey];
    const { transKey, hidden, assetType, regional, materialSet, options } = attr;

    if (hidden) {
      return null;
    }

    // Don't show attribute if it only has one material option
    if (assetType === "material") {
      if (
        regional && materialsMap.SETS[materialSet][config._region]?.length === 1
      ) {
        return null;
      } else if (materialsMap.SETS[materialSet]?.length === 1) {
        return null;
      }
    }

    if (attr.hasOwnProperty("constrained_by")) {
      try {
        attr.constrained_by.forEach((constraint) => {
          const { part, attr, values } = constraint;
          const constrainerValue = config[part][attr];
          const isAllowed = values.find((el) => el === constrainerValue);
          if (!isAllowed) throw AttrDisabledException;
        });
      } catch (error) {
        if (error !== AttrDisabledException) {
          throw error;
        } else {
          return null;
        }
      }
    }

    const selectedValue = config[partKey][attrKey];

    if (!selectedValue) {
      // console.log(`Skipped ${partKey}.${attrKey}`);
      return null;
    }

    let selectedValueLabel;
    let selectedValueColor;

    if (assetType === "material") {
      const selectedMaterial = materialsMap.MATERIALS[selectedValue];
      if (selectedMaterial) {
        selectedValueColor = selectedMaterial.color;
        selectedValueLabel = t(`materials.${selectedMaterial.transKey}`);
      }
    } else {
      const isRegionalOption = regional || false;
      const opts = isRegionalOption ? options[config._region] : options;

      const selectedOption = opts.find((el) => el.value === selectedValue);

      if (selectedOption.hidden) {
        return null;
      }

      if (selectedOption) {
        selectedValueLabel = t(`parts:${partKey}.${selectedOption.transKey}`);
      }
    }

    return (
      <div key={attrKey} className="attribute">
        <span className="key">{t(`parts:${partKey}.${transKey}`)}</span>
        <span className="val">
          {selectedValueLabel}
          {assetType === "material" && (
            <div
              style={{ backgroundColor: selectedValueColor }}
              className="color"
            ></div>
          )}
        </span>
      </div>
    );
  };

  return (
    <section className="app-nitrum-section">
      <div className="head">
        <div className="part" onClick={() => dispatch(openDrawer(partKey))}>
          <IconPencil className="icon" />
          {t(`parts:${partKey}._part_name`)}
        </div>
      </div>
      <div className="body">
        {Object.keys(attributes).map((attrKey) => {
          return getAttribute(attrKey);
        })}
      </div>
    </section>
  );
}

export default function YourNitrum() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nitrumOpen = useSelector((state) => state.nav.nitrumOpen);
  const prevNitrumOpen = usePrevious(nitrumOpen);
  const [nitrumOpenDelayed, setNitrumOpenDelayed] = useState(nitrumOpen);

  useEffect(() => {
    // Delay unsetting yourNitrumVisible so out transition can play
    if (prevNitrumOpen && !nitrumOpen) {
      const timer = setTimeout(() => {
        setNitrumOpenDelayed(nitrumOpen);
      }, 500);
      return () => clearTimeout(timer);
    } else if (prevNitrumOpen !== nitrumOpen) {
      setNitrumOpenDelayed(nitrumOpen);
    }
  }, [nitrumOpen, prevNitrumOpen]);

  return (
    <div className={`app-nitrum ${nitrumOpen ? "open" : ""}`}>
      <div className="drawer-handle">
        <div className="handle-line"></div>
        <button
          className="btn btn-circle handle-btn"
          onClick={() => dispatch(closeNitrum())}
          title={t("hide")}
        >
          <IconCaret className="icon" />
        </button>
      </div>
      <div className="drawer-content">
        <div className="scrollarea">
          <div className="app-nitrum-head">
            <img src={nitrumLogo} className="logo-nitrum" alt="Nitrum" />
            <img src={sunriseLogo} className="logo-sm" alt="Sunrise Medical" />
          </div>
          <h1 className="app-nitrum-title">{t("my_summary")}</h1>
          {nitrumOpenDelayed &&
            Object.keys(configMap).map((partKey) => {
              return <Section key={partKey} partKey={partKey} />;
            })
          }
          <NavNitrum desktop={false} />
        </div>
      </div>
    </div>
  );
}
